import { Fragment, useState, useEffect, useRef } from 'react';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid2';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import AddIcon from '@mui/icons-material/Add';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import SearchIcon from '@mui/icons-material/Search';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import api from 'utils/api';
import { PageLoader, PageError } from 'components';

// components and parts
import { DOLPHIN_API } from 'shared/constants/apis';
import CreateTagModal from './CreateTagModal';
import TagCard from './TagCard';

//todo:dacao  add feature to rank tags in an order;

const IWGDashboardTag = () => {
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(true);
  const [organizationsError, setOrganizationsError] = useState(null);
  const [tagData, setTagData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [createTagModalOpen, setCreateTagModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const tagRefs = useRef({});

  // Fetch organizations on component mount
  useEffect(() => {
    const fetchOrganizations = async () => {
      setOrganizationsLoading(true);
      try {
        const orgs = await api.get(DOLPHIN_API.ADMIN_ORGS, {});
        setOrganizations(orgs);
        setOrganizationsLoading(false);
      } catch (err) {
        console.error('Error fetching organizations:', err);
        setOrganizationsError(err);
        setOrganizationsLoading(false);
      }
    };

    fetchOrganizations();
  }, []);

  // Fetch tags when selected organization changes
  useEffect(() => {
    if (selectedOrganization) {
      fetchTagsForOrganization(selectedOrganization);
    } else {
      // Clear tag data when no organization is selected
      setTagData(null);
      setError(null);
    }
  }, [selectedOrganization]);

  // Function to fetch tags for a selected organization
  const fetchTagsForOrganization = async (orgId) => {
    if (!orgId) return;

    setIsLoading(true);
    setError(null);
    try {
      // Make API call to get tags for this organization
      const response = await api.get(DOLPHIN_API.ADMIN_TAGS, {
        params: {
          organizationId: orgId,
        },
      });
      const { tags } = response;
      // Verify that the data has the expected structure
      if (Array.isArray(tags)) {
        setTagData(tags);
      } else {
        console.error('Unexpected data format received:', tags);
        setError(new Error('Received invalid data format from server'));
      }
      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching tags:', err);
      setError(err);
      setIsLoading(false);
    }
  };

  // Handle organization change
  const handleOrganizationChange = (event) => {
    const orgId = event.target.value;
    setSelectedOrganization(orgId);
    setSearchTerm(''); // Clear search when changing organization
  };

  // Handle opening create tag modal
  const handleOpenCreateTag = () => {
    setCreateTagModalOpen(true);
  };

  // Handle tag creation
  const handleTagCreated = () => {
    // Refresh the tag list after creating a new tag
    fetchTagsForOrganization(selectedOrganization);
  };

  // Handle tag save (placeholder for future implementation)
  const handleSaveTag = async (updatedTag) => {
    // TODO: Implement API call to save tag
    await api.put(DOLPHIN_API.ADMIN_TAGS, {
      data: { updatedTag },
    });
    // Refresh tags after saving
    fetchTagsForOrganization(selectedOrganization);
  };

  // Handle tag search
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm('');
  };

  // Get filtered tags based on search term
  const getFilteredTags = () => {
    if (!tagData) return [];
    if (!searchTerm) return tagData;

    const searchLower = searchTerm.toLowerCase();
    return tagData.filter((tag) => {
      const content = tag.content || {};
      const name = content.name || '';

      // Search in tag name and display names
      if (name.toLowerCase().includes(searchLower)) return true;

      const displayNames = content.displayName || {};
      return Object.values(displayNames).some(
        (displayName) =>
          displayName && displayName.toLowerCase().includes(searchLower)
      );
    });
  };

  // Scroll to specific tag
  const scrollToTag = (tagId) => {
    if (tagRefs.current[tagId]) {
      tagRefs.current[tagId].scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      // Highlight the card briefly
      const element = tagRefs.current[tagId];
      element.style.boxShadow = '0 0 0 2px #1976d2';
      setTimeout(() => {
        element.style.boxShadow = '';
      }, 2000);
    }
  };

  // Sort tags alphabetically for tag navigation
  const getSortedTags = () => {
    if (!tagData) return [];

    return [...tagData].sort((a, b) => {
      const nameA = (a.content?.name || '').toLowerCase();
      const nameB = (b.content?.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  };

  // Show loading or error states for organizations
  if (organizationsLoading || organizations.length === 0) return <PageLoader />;
  if (organizationsError)
    return (
      <PageError
        errorCode={error.response.status}
        customErrorMessage={error.response.data.message}
        customErrorDescription={error.response.data.description}
      />
    );

  // Render tag navigation
  const renderTagNavigation = () => {
    if (!tagData || tagData.length === 0) return null;

    const sortedTags = getSortedTags();

    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}>
          <Typography variant="subtitle1" fontWeight="medium">
            Quick Navigation - All Tags ({sortedTags.length})
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Paper
              component="form"
              sx={{
                p: '1px 1px',
                display: 'flex',
                alignItems: 'center',
                width: 450,
                border: '1px solid #e0e0e0',
              }}>
              <InputBase
                sx={{ ml: 1, flex: 1 }}
                placeholder="Search name..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
              {searchTerm ? (
                <IconButton
                  size="small"
                  aria-label="clear"
                  onClick={clearSearch}>
                  ×
                </IconButton>
              ) : (
                <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              )}
            </Paper>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            maxHeight: '150px',
            overflowY: 'auto',
            p: 1,
          }}>
          {sortedTags.map((tag) => {
            const content = tag.content || {};
            const tagName = content.name || `ID: ${tag.id}`;
            const isActive = tag.isActive !== undefined ? tag.isActive : true;

            return (
              <Chip
                key={tag.id}
                label={
                  <Box
                    sx={{
                      textAlign: 'center',
                      maxWidth: '100%',
                      overflow: 'hidden',
                    }}>
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{
                        fontWeight: 'medium',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                      {tagName}
                    </Typography>

                    {/* Display language names in specific order: Japanese, English, Chinese, Indonesian */}
                    {['ja-JP', 'en-US', 'zh-CN', 'id-ID'].map((langCode) =>
                      content.displayName && content.displayName[langCode] ? (
                        <Typography
                          key={langCode}
                          variant="caption"
                          component="div"
                          sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {content.displayName[langCode]}
                        </Typography>
                      ) : null
                    )}
                  </Box>
                }
                onClick={() => scrollToTag(tag.id)}
                variant="outlined"
                color={isActive ? 'success' : 'error'}
                sx={{
                  cursor: 'pointer',
                  background: 'transparent',
                  border: '1px solid',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  },
                  height: 'auto',
                  '& .MuiChip-label': {
                    display: 'block',
                    whiteSpace: 'normal',
                    padding: '4px 8px',
                    maxWidth: '100%',
                  },
                }}
              />
            );
          })}
        </Box>
      </Paper>
    );
  };

  // Render the tag card section
  const renderTagSection = () => {
    if (!selectedOrganization) {
      // Don't render anything below if no organization is selected
      return null;
    }

    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <PageLoader />
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="error">
            Error loading tags: {error.message}
          </Typography>
        </Box>
      );
    }

    const selectedOrg = organizations.find(
      (org) => org.id.toString() === selectedOrganization.toString()
    );
    const orgName = selectedOrg ? selectedOrg.name : 'Unknown';
    const filteredTags = getFilteredTags();

    if (!tagData || tagData.length === 0) {
      return (
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '300px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            border: '1px dashed #ccc',
          }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            No tags found for {orgName}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Create a new tag to get started
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateTag}
            sx={{ color: '#ffffff' }}>
            Create Tag
          </Button>
        </Box>
      );
    }

    if (filteredTags.length === 0) {
      return (
        <Box>
          {renderTagNavigation()}
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              backgroundColor: '#f9f9f9',
              borderRadius: '8px',
              border: '1px dashed #ccc',
            }}>
            <Typography variant="h6">No tags match your search</Typography>
            <Button
              variant="outlined"
              color="primary"
              onClick={clearSearch}
              sx={{ mt: 2 }}>
              Clear Search
            </Button>
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ width: '100%', overflow: 'hidden' }}>
        {renderTagNavigation()}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            px: 2,
          }}>
          <Typography variant="h6">
            {`Filtered Tags:   ${filteredTags.length} of ${tagData.length}`}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateTag}
            sx={{ color: '#ffffff' }}>
            Create New Tag
          </Button>
        </Box>

        <Grid container spacing={3} sx={{ px: 2, mb: 2 }}>
          {filteredTags.map((tag) => (
            <Grid size={6} key={tag.id}>
              <Box ref={(el) => (tagRefs.current[tag.id] = el)}>
                <TagCard tag={tag} onSave={handleSaveTag} />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  return (
    <Fragment>
      <Card sx={{ margin: 4, marginTop: 3, overflow: 'visible' }}>
        <Box sx={{ overflow: 'hidden' }}>
          <Grid container spacing={2} sx={{ p: 3 }}>
            <Grid size={12}>
              <Typography variant="h4" gutterBottom>
                Tag Management
              </Typography>
              <Typography variant="body1" gutterBottom>
                Manage organization-specific tags and pricing rules
              </Typography>
            </Grid>
            <Grid size={4}>
              <FormControl fullWidth>
                <InputLabel id="organization-select-label">
                  Select Organization
                </InputLabel>
                <Select
                  labelId="organization-select-label"
                  id="organization-select"
                  value={selectedOrganization}
                  label="Select Organization"
                  onChange={handleOrganizationChange}>
                  {organizations.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {renderTagSection()}
        </Box>
      </Card>

      {selectedOrganization && (
        <CreateTagModal
          isOpen={createTagModalOpen}
          onClose={() => setCreateTagModalOpen(false)}
          organizationId={selectedOrganization}
          onTagCreated={handleTagCreated}
        />
      )}
    </Fragment>
  );
};

export default IWGDashboardTag;
